import { Injectable, Type, ViewContainerRef } from '@angular/core';

import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { map, merge, Observable, take } from 'rxjs';
import { filter } from 'rxjs/operators';

import { BreakpointFacade } from '@core/facades';
import { DIALOG_ANIMATION_CLASSES, ModalsNames } from '@data/const';
import {
  GetMobileAppDialogComponent,
} from '@shared/components/dialogs/get-mobile-app-dialog/get-mobile-app-dialog.component';
import { BannerPictureType, ConfirmModalData, CurtainData } from '@core/models';
import { CurtainComponent } from '@shared/components';
import { QrDialogComponent } from '@shared/components/dialogs/qr-dialog/qr-dialog.component';
import { AnalyticsEventsEnum } from '@core/models/analytics.model';
import { ConfirmDialogComponent } from '@shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { AuthComponent } from '@modules/auth/components/auth/auth.component';

@Injectable({ providedIn: 'root' })
export class OverlayService {
  private viewContainerRef!: ViewContainerRef;

  constructor(
    private readonly ngxSmartModalService: NgxSmartModalService,
    private readonly breakpointFacade: BreakpointFacade,
  ) {
    breakpointFacade.isDesktop$
      .pipe(
        filter(Boolean),
      )
      .subscribe(() => {
        this.close(ModalsNames.Curtain);
      });
  }

  public openDialog<T = null>(
    dialogName: ModalsNames,
    component: Type<any>,
    data?: Partial<T>,
    customClasses?: string,
  ): NgxSmartModalComponent {
    const animationClass = customClasses ?? this.getAnimationClass();
    let dialog: NgxSmartModalComponent;

    try {
      dialog = this.ngxSmartModalService.get(dialogName);
      dialog.removeCustomClass(Object.values(DIALOG_ANIMATION_CLASSES).join(' '));
      dialog.addCustomClass(animationClass);
    } catch (e) {
      dialog = this.ngxSmartModalService.create(dialogName, component, this.viewContainerRef, {
        closable: false,
        customClass: 'cheelee-dialog ' + animationClass,
      });
    }

    if (data) {
      dialog.setData(data, true);
    }

    this.subscribeOnModalClose(dialog);

    return dialog.visible ? dialog : dialog.open();
  }

  public openGetAppDialog(action: AnalyticsEventsEnum, pictureData?: BannerPictureType): void {
    this.openDialog(ModalsNames.GetApp, GetMobileAppDialogComponent, { action, pictureData }, 'fade-animation');
  }

  public openQrDialog(): void {
    this.openDialog(
      ModalsNames.Qr,
      QrDialogComponent,
      null,
      `${DIALOG_ANIMATION_CLASSES.fade} ${DIALOG_ANIMATION_CLASSES.transparentBackground}`
    );
  }

  public close(modalName: ModalsNames): void {
    try {
      if (this.getModalInstance(modalName)) {
        this.ngxSmartModalService.close(modalName);
      }
    } catch {}
  }

  public closeAll(): void {
    Object.values(ModalsNames).forEach((modalName) => this.close(modalName));
  }

  public getModalInstance(modalName: ModalsNames): NgxSmartModalComponent | null {
    let modalInstance!: NgxSmartModalComponent | null;

    try {
      modalInstance = this.ngxSmartModalService.get(modalName);
    } catch {}

    return modalInstance;
  }

  public getModalData<T = unknown>(modalName: ModalsNames): T {
    return this.ngxSmartModalService.getModalData(modalName) as T;
  }

  public getOpenedDialogs<T = unknown>(): T[] {
    return this.ngxSmartModalService.getOpenedModals() as T[];
  }

  public removeModalInstance(modalName: ModalsNames): void {
    this.ngxSmartModalService.removeModal(modalName);
  }

  public setDialogContainer(viewContainerRef: ViewContainerRef): void {
    if (this.viewContainerRef) {
      return;
    }

    this.viewContainerRef = viewContainerRef;
  }

  public resetModalData(modalName: ModalsNames): void {
    try {
      const modalInstance = this.getModalInstance(modalName);

      if (modalInstance?.hasData()) {
        this.ngxSmartModalService.resetModalData(modalName);
      }
    } catch (e) {}
  }

  public openAuthDialog(): void {
    this.openDialog(
      ModalsNames.Auth,
      AuthComponent,
      null,
      `${DIALOG_ANIMATION_CLASSES.mobileFullscreen} ${DIALOG_ANIMATION_CLASSES.leftToRight}`,
    );
  }

  private getAnimationClass(): string {
    return ' ' + this.breakpointFacade.isMobile()
      ? DIALOG_ANIMATION_CLASSES.leftToRight
      : DIALOG_ANIMATION_CLASSES.fade;
  }

  private subscribeOnModalClose(modalInstance: NgxSmartModalComponent): void {
    modalInstance.onAnyCloseEventFinished.pipe(take(1)).subscribe(() => {
      const modalName = modalInstance.identifier as ModalsNames;
      this.resetModalData(modalName);
      this.removeModalInstance(modalName);
    });
  }

  public openConfirmModal(data: ConfirmModalData): Observable<boolean> {
    let confirmModal: NgxSmartModalComponent;

    try {
      confirmModal = this.ngxSmartModalService.get(ModalsNames.Confirm);
    } catch (e) {
      confirmModal = this.ngxSmartModalService.create(
        ModalsNames.Confirm,
        ConfirmDialogComponent,
        this.viewContainerRef,
        {
          closable: false,
          customClass: 'cheelee-confirm-modal nsm-centered ' + DIALOG_ANIMATION_CLASSES.fade,
        },
      );
    }

    confirmModal.setData(data, true);
    this.subscribeOnModalClose(confirmModal);

    if (!confirmModal.visible) {
      confirmModal.open(true);
    }

    return merge(
      confirmModal.onClose.pipe(take(1)),
      confirmModal.onAnyCloseEvent.pipe(
        map(() => false),
        take(1),
      ),
    ).pipe(take(1));
  }

  public openCurtain<T>(data: CurtainData<T>): NgxSmartModalComponent {
    let curtain: NgxSmartModalComponent;

    try {
      curtain = this.ngxSmartModalService.get(ModalsNames.Curtain);
    } catch (e) {
      curtain = this.ngxSmartModalService.create(ModalsNames.Curtain, CurtainComponent, this.viewContainerRef, {
        closable: false,
        customClass: 'cheelee-curtain',
      });
    }

    curtain.setData(data, true);
    this.subscribeOnModalClose(curtain);
    return curtain.visible ? curtain : curtain.open(true);
  }
}
